import React, { useEffect, useMemo, useRef, useState } from 'react';
import FeedCard from './FeedCard';
import { FitSessionWithProducts } from '../services/fitSessionsAPI';
import { pickDisplayImage } from '../utils/image';
import { getCategoryLabel } from '../constants/categories';

interface FeedViewProps {
  sessions: FitSessionWithProducts[];
  onOpenSession: (id: string) => void;
  onToggleFavorite: (id: string) => void;
  onAskDelete: (id: string) => void;
  locale?: string; // e.g. 'nl-NL'
  lang?: 'nl' | 'en';
}

const PAGE = 12;

const FeedView: React.FC<FeedViewProps> = ({
  sessions,
  onOpenSession,
  onToggleFavorite,
  onAskDelete,
  locale = 'nl-NL',
  lang = 'nl',
}) => {
  // Only completed sessions should appear in the visual feed (photo-first)
  const feedSessions = useMemo(() => sessions.filter(s => s.status === 'COMPLETED'), [sessions]);

  const [visible, setVisible] = useState(PAGE);
  const sentinelRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    setVisible(PAGE);
  }, [feedSessions.length]);

  // Infinite scroll using IntersectionObserver
  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;
    const obs = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting) {
          setVisible((v) => Math.min(v + PAGE, feedSessions.length));
        }
      });
    }, { root: null, rootMargin: '200px', threshold: 0 });
    obs.observe(sentinel);
    return () => obs.disconnect();
  }, [feedSessions.length]);

  const [sheetId, setSheetId] = useState<string | null>(null);

  const openProduct = (url?: string | null) => {
    if (url) window.open(url, '_blank', 'noopener,noreferrer');
  };

  const items = feedSessions.slice(0, visible);

  return (
    <div className="max-w-2xl mx-auto p-0 sm:p-6">
      <div className="flex flex-col gap-0 sm:gap-6">
        {items.map((s) => {
          const first = s.products?.[0];
          const productName = first?.product_name || 'Onbekend product';
          const price = first?.product_price || null;
          const retailer = s.shop?.name || s.retailer?.shop_name || 'Onbekende winkel';
          const img = pickDisplayImage(s.status, s.generated_image_url, first?.product_image_url);
          const dateStr = new Date(s.created_at).toLocaleDateString(locale);
          const category = s.category ? getCategoryLabel(s.category, lang) : null;

          return (
            <div key={s.id}>
              <FeedCard
                imageUrl={img}
                status={s.status}
                isFavorite={!!s.favorite}
                productName={productName}
                price={price}
                retailer={retailer}
                date={dateStr}
                category={category || undefined}
                onOpenSession={() => onOpenSession(s.id)}
                onToggleFavorite={() => onToggleFavorite(s.id)}
                onLongPress={() => setSheetId(s.id)}
              />
            </div>
          );
        })}
        <div ref={sentinelRef} />
      </div>

      {/* Bottom sheet */}
      {sheetId && (
        <div className="fixed inset-0 z-50" onClick={() => setSheetId(null)}>
          <div className="absolute inset-0 bg-black/40" />
          <div className="absolute left-0 right-0 bottom-0 bg-white rounded-t-2xl shadow-2xl p-4 pb-6" onClick={(e) => e.stopPropagation()}>
            <div className="mx-auto h-1 w-12 rounded-full bg-gray-300 mb-3" />
            <div className="space-y-2">
              <button
                className="w-full text-left px-4 py-3 rounded-lg hover:bg-gray-50"
                onClick={() => { const id = sheetId; setSheetId(null); if (id) onOpenSession(id); }}
              >
                👁 Bekijken
              </button>
              <button
                className="w-full text-left px-4 py-3 rounded-lg hover:bg-gray-50"
                onClick={() => {
                  const s = feedSessions.find(x => x.id === sheetId);
                  setSheetId(null);
                  if (s) openProduct(s.products?.[0]?.product_url);
                }}
              >
                🛍 Bekijk product
              </button>
              <button
                className="w-full text-left px-4 py-3 rounded-lg hover:bg-gray-50 text-red-600"
                onClick={() => { const id = sheetId; setSheetId(null); if (id) onAskDelete(id); }}
              >
                🗑 Verwijderen
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FeedView;
